<script setup lang="ts">
import { personalInfo, aboutContent } from '../../data/personal'
import { revealDirective } from '../../composables/useReveal'

defineOptions({ directives: { reveal: revealDirective } })

const { philosophy, futurePlans } = aboutContent
const portrait = new URL('../../assets/images/me.webp', import.meta.url).href
</script>

<template>
  <section id="about" class="section cv-auto">
    <div class="wrap">
      <div class="section-head" v-reveal>
        <h2 class="display display-3">About</h2>
        <p class="eyebrow">Abraham Michael</p>
      </div>

      <div class="layout">
        <!-- ---------- narrative ---------- -->
        <div class="narrative" v-reveal="{ staggerInterval: 90 }">
          <p class="standfirst">
            I build software for places where a failed request means a person
            waits. That constraint shapes everything else.
          </p>

          <p class="prose-body">
            I hold a BSc in Information Technology from Mekelle University
            and serve as <strong>co-founder & lead software engineer at Neo Technologies</strong>, delivering production
            software across healthcare, hospitality, logistics, and transportation. Most of what I ship spans
            Vue 3, NestJS, Flutter, Expo (React Native), Node.js, and MongoDB — focusing on thoughtful system design,
            scalable architectures, and pragmatic engineering decisions.
          </p>

          <p class="prose-body">
            {{ philosophy.content }}
          </p>

          <h3 class="eyebrow">Looking ahead</h3>
          <p class="prose-body">
            {{ futurePlans.content }}
          </p>
        </div>

        <!-- ---------- margin column ---------- -->
        <aside class="margin" v-reveal="{ staggerInterval: 110 }">
          <figure class="portrait">
            <img
              :src="portrait"
              alt="Portrait of Abraham Michael"
              loading="lazy"
              decoding="async"
            />
            <figcaption class="meta">{{ personalInfo.contact.location }}</figcaption>
          </figure>

          <dl class="facts">
            <div>
              <dt class="eyebrow">Studying</dt>
              <dd>{{ personalInfo.about.education }}</dd>
            </div>
            <div>
              <dt class="eyebrow">Building</dt>
              <dd>{{ personalInfo.about.company }}</dd>
            </div>
            <div>
              <dt class="eyebrow">Availability</dt>
              <dd>{{ personalInfo.contact.availability }}</dd>
            </div>
          </dl>

          <div class="offhours">
            <h3 class="eyebrow">Off the clock</h3>
            <ul>
              <li v-for="interest in personalInfo.about.interests" :key="interest">
                {{ interest }}
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<style scoped>
.section-head {
  margin-bottom: clamp(2rem, 5vw, 3.5rem);
}

.layout {
  display: grid;
  gap: clamp(2.5rem, 6vw, 5rem);
  grid-template-columns: 1fr;
}
@media (min-width: 940px) {
  .layout {
    /* aside capped so the portrait doesn't balloon inside the wide wrap */
    grid-template-columns: minmax(0, 1.55fr) minmax(16rem, 26rem);
  }
}

/* ---------- narrative ---------- */
.standfirst {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: var(--display-3);
  line-height: 1.24;
  letter-spacing: -0.025em;
  color: var(--c-ink);
  margin: 0 0 1.8rem;
  max-width: 32ch;
  text-wrap: balance;
}
.narrative .prose-body {
  margin: 0 0 1.4rem;
}
.narrative h3 {
  margin: 2.2rem 0 0.9rem;
}

/* ---------- margin column ---------- */
.portrait {
  margin: 0 0 2rem;
}
.portrait img {
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  object-position: center top;
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  filter: grayscale(1) contrast(1.04);
  transition: filter 0.5s ease;
}
.portrait:hover img {
  filter: none;
}
.portrait figcaption {
  margin-top: 0.6rem;
  color: var(--c-ink-3);
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.facts {
  margin: 0 0 2rem;
  border-top: 1px solid var(--c-rule);
}
.facts div {
  padding: 0.85rem 0;
  border-bottom: 1px solid var(--c-rule);
}
.facts dt {
  margin-bottom: 0.3rem;
}
.facts dd {
  margin: 0;
  color: var(--c-ink);
  font-size: 0.95rem;
}

.offhours ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.offhours li {
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  padding: 0.2rem 0.55rem;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--c-ink-2);
}
</style>
